import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Phone, MapPin, Send, ArrowRight, CheckCircle, ClipboardList, Monitor, Users } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function ContactPage() {
  const [form, setForm] = useState({
    contact_name: '', email: '', phone: '', school_name: '', school_website: '', city: '',
    num_students: '', grades: '', message: '',
  });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Auto-prefix https:// if missing
    let website = form.school_website.trim();
    if (website && !website.match(/^https?:\/\//i)) {
      website = 'https://' + website;
    }
    // Basic URL validation
    try {
      new URL(website);
    } catch {
      toast.error('Please enter a valid school website');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/demo-requests', {
        school_name: form.school_name,
        contact_name: form.contact_name,
        email: form.email,
        phone: form.phone,
        city: form.city,
        school_website: website,
        num_students: form.num_students || null,
        grades: form.grades || null,
        message: form.message || null,
      });
      setSent(true);
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="contact-page">
        <section className="py-14 sm:py-20">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center animate-fade-in-up">
            <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-emerald-500" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-[#1F2937] font-['Outfit'] tracking-tight" data-testid="success-title">
              You're all set!
            </h1>
            <p className="mt-4 text-base sm:text-lg text-[#4B5563] max-w-md mx-auto leading-relaxed">
              We've received your request.<br />
              We'll connect with you within the next 24 hours to schedule a demo for your school.
            </p>

            <div className="mt-10 bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 text-left" data-testid="what-happens-next">
              <h3 className="text-lg font-bold text-[#1F2937] font-['Outfit'] mb-5">What happens next?</h3>
              <div className="space-y-4">
                {[
                  { icon: ClipboardList, text: 'We review your school details' },
                  { icon: Monitor, text: 'We schedule a quick demo (15\u201320 mins)' },
                  { icon: Users, text: 'You see how it works for your students' },
                ].map((step, i) => (
                  <div key={i} className="flex items-start gap-3.5">
                    <div className="w-9 h-9 rounded-xl bg-[#FFF0ED] flex items-center justify-center shrink-0 mt-0.5">
                      <step.icon className="h-4.5 w-4.5 text-[#F97D6A]" />
                    </div>
                    <p className="text-sm sm:text-base text-[#374151] leading-relaxed pt-1.5">{step.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8">
              <Link to="/careers">
                <Button className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl text-sm font-semibold px-6 py-3 h-auto" data-testid="explore-after-submit-btn">
                  Explore Careers
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="contact-page">
      <section className="py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 animate-fade-in-up">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#F97D6A] bg-[#FFF0ED] px-3 py-1.5 rounded-full mb-6">
              Get Started
            </span>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#1F2937] font-['Outfit']">
              Request a Demo
            </h1>
            <p className="mt-4 text-base sm:text-lg text-[#4B5563] max-w-xl mx-auto">
              Tell us about your school and we'll set up a personalized walkthrough.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Contact Info */}
            <div className="space-y-4 animate-fade-in-up stagger-1">
              {[
                { icon: Mail, label: 'Email', value: 'hello@careerspov.com' },
                { icon: Phone, label: 'Phone', value: '+91 9699471249' },
                { icon: MapPin, label: 'Address', value: 'Pune, Maharashtra, India' },
              ].map((c) => (
                <div key={c.label} className="bg-white rounded-2xl border border-gray-200 p-5 flex items-start gap-4" data-testid={`contact-info-${c.label.toLowerCase()}`}>
                  <div className="w-10 h-10 rounded-xl bg-[#FFF0ED] flex items-center justify-center shrink-0">
                    <c.icon className="h-5 w-5 text-[#F97D6A]" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF]">{c.label}</p>
                    <p className="text-sm text-[#1F2937] font-medium mt-0.5">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Form */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-6 md:p-8 animate-fade-in-up stagger-2">
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#1F2937] font-medium">Contact Name <span className="text-red-400">*</span></Label>
                    <Input value={form.contact_name} onChange={update('contact_name')} placeholder="Full name" required className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <Label className="text-[#1F2937] font-medium">Email <span className="text-red-400">*</span></Label>
                    <Input type="email" value={form.email} onChange={update('email')} placeholder="you@school.edu" required className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="contact-email-input" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#1F2937] font-medium">Phone <span className="text-red-400">*</span></Label>
                    <Input type="tel" value={form.phone} onChange={update('phone')} placeholder="+91 98765 43210" required className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="contact-phone-input" />
                  </div>
                  <div>
                    <Label className="text-[#1F2937] font-medium">School Name <span className="text-red-400">*</span></Label>
                    <Input value={form.school_name} onChange={update('school_name')} placeholder="e.g. DPS Whitefield" required className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="contact-school-input" />
                  </div>
                </div>

                <div>
                  <Label className="text-[#1F2937] font-medium">School Website <span className="text-red-400">*</span></Label>
                  <Input type="url" value={form.school_website} onChange={update('school_website')} placeholder="https://www.schoolname.com" required className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="contact-website-input" />
                </div>

                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-[#1F2937] font-medium">City <span className="text-red-400">*</span></Label>
                    <Input value={form.city} onChange={update('city')} placeholder="e.g. Pune" required className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="contact-city-input" />
                  </div>
                  <div>
                    <Label className="text-[#1F2937] font-medium">Number of Students</Label>
                    <Input value={form.num_students} onChange={update('num_students')} placeholder="e.g. 500" className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="contact-students-input" />
                  </div>
                  <div>
                    <Label className="text-[#1F2937] font-medium">Grades</Label>
                    <Input value={form.grades} onChange={update('grades')} placeholder="e.g. 8-12" className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="contact-grades-input" />
                  </div>
                </div>

                <div>
                  <Label className="text-[#1F2937] font-medium">Message</Label>
                  <textarea
                    value={form.message}
                    onChange={update('message')}
                    placeholder="Tell us about your school or what you're looking for..."
                    rows={3}
                    className="mt-1.5 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#F97D6A]/50 focus:border-[#F97D6A] resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold px-6 py-3 h-auto disabled:opacity-60"
                  data-testid="contact-submit-btn"
                >
                  <Send className="mr-2 h-4 w-4" />
                  {submitting ? 'Submitting...' : 'Request Demo'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
